import React, { useState, useEffect, useCallback, useRef } from 'react';
import { XCircle, X, Upload, Image as ImageIcon, FileText, File } from 'lucide-react';
import api from 'services/api';
import { LoadingSpinner } from 'components/common';
import NotificationToast from 'components/common/NotificationToast';
import Pagination from 'components/Pagination';

import ArquivoItem from './contasReceber/components/ArquivoItem';
import HeaderStats from './contasReceber/components/HeaderStats';
import FiltrosOtimizados from './contasReceber/components/FiltrosOtimizados';
import TabelaContasReceber from './contasReceber/components/TabelaContasReceber';
import GraficoContasPorDia from './contasReceber/components/GraficoContasPorDia';
import GraficoContasPorMes from './contasReceber/components/GraficoContasPorMes';
import { formatarMoeda, processarContas } from './contasReceber/utils';
import { processarDadosGraficoDia } from './contasReceber/utils/processadoresGraficos'; // Fallback caso backend não retorne
import { COMPANY_STORAGE_KEY } from 'types/company.js';
import FinanceSectionHeader from './financeShared/FinanceSectionHeader';

const REQUEST_DEDUPE_WINDOW_MS = 1200;
const requestDedupeCache = new Map();

const shouldBlockDuplicateRequest = (key) => {
  const now = Date.now();
  const lastRequestAt = requestDedupeCache.get(key);

  if (lastRequestAt && now - lastRequestAt < REQUEST_DEDUPE_WINDOW_MS) {
    return true;
  }

  requestDedupeCache.set(key, now);
  return false;
};

const ContasReceber = () => {
  const MOBILE_BREAKPOINT = 768;
  const MOBILE_PER_PAGE = 20;
  const DESKTOP_PER_PAGE = 100;
  const [loading, setLoading] = useState(false);
  const [contasReceber, setContasReceber] = useState([]);
  const [mesesProximos3, setMesesProximos3] = useState([]); // Agora armazena dados agregados por mês
  const [contasRecebidas, setContasRecebidas] = useState([]);
  const [stats, setStats] = useState({ total_vencidas: 0, total_proximas: 0, total_geral: 0 });
  const [graficoDias, setGraficoDias] = useState([]); // Dados do gráfico por dias da semana
  
  // Flags para evitar requisições simultâneas
  const [carregandoRecebidas, setCarregandoRecebidas] = useState(false);
  const [carregandoEmAberto, setCarregandoEmAberto] = useState(false);
  const [ultimaRequisicaoRecebidas, setUltimaRequisicaoRecebidas] = useState(null);
  const [ultimaRequisicaoEmAberto, setUltimaRequisicaoEmAberto] = useState(null);
  const [filtroAtivo, setFiltroAtivo] = useState('todas');
  const [busca, setBusca] = useState('');

  const today = new Date();
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const pad = (n) => String(n).padStart(2, '0');
  const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const getMesAtualRange = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { presetId: 'mes_atual', start: toISO(start), end: toISO(end) };
  };
  const [range, setRange] = useState({ presetId: 'mes_atual', start: toISO(startOfMonth), end: toISO(endOfMonth) });

  const [notification, setNotification] = useState(null);
  const [modalAberto, setModalAberto] = useState(false);
  const [contaSelecionada, setContaSelecionada] = useState(null);
  const [arquivosNovos, setArquivosNovos] = useState([]);
  const [uploadingArquivos, setUploadingArquivos] = useState(false);
  const detalhesModalRef = useRef(null);
  const detalhesModalCloseRef = useRef(null);
  const filtroInicializadoRef = useRef(false);
  const filtrosDebounceInicializadoRef = useRef(false);
  const perPageInicializadoRef = useRef(false);
  const mobileInicializadoRef = useRef(false);
  
  // Paginação
  const [page, setPage] = useState(1);
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth < MOBILE_BREAKPOINT : false));
  const [perPage, setPerPage] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT ? MOBILE_PER_PAGE : DESKTOP_PER_PAGE
  );
  const [pagination, setPagination] = useState(null);

  const [filtroArquivosRecebidas, setFiltroArquivosRecebidas] = useState('todas');
  const [filtroArquivosTodas, setFiltroArquivosTodas] = useState('todas');
  const [filtroArquivosVencidas, setFiltroArquivosVencidas] = useState('todas');
  const [filtroArquivosHoje, setFiltroArquivosHoje] = useState('todas');
  // Filtro por tipo de recebimento
  const [filtroTipoRecebimento, setFiltroTipoRecebimento] = useState('todos');
  const preloadInicialExecutadoRef = useRef(false);
  const debounceRangeInicializadoRef = useRef(false);
  const carregarEmAbertoRef = useRef(() => {});
  const carregarRecebidasRef = useRef(() => {});
  const carregarProximos3MesesRef = useRef(() => {});

  // Helper para normalizar strings nas comparações
  const mergeById = (prev = [], next = []) => {
    const map = new Map();
    prev.forEach((item) => map.set(String(item.id), item));
    next.forEach((item) => map.set(String(item.id), item));
    return Array.from(map.values());
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const abrirModal = (conta) => {
    setContaSelecionada(conta);
    setModalAberto(true);
  };
  const fecharModal = () => {
    setModalAberto(false);
    setContaSelecionada(null);
    setArquivosNovos([]);
  };

  const handleDetalhesModalKeyDown = (event) => {
    if (!modalAberto) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      fecharModal();
      return;
    }
    if (event.key !== 'Tab') return;
    const modalNode = detalhesModalRef.current;
    if (!modalNode) return;
    const focusables = Array.from(
      modalNode.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
    ).filter((element) => !element.hasAttribute('disabled'));

    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  useEffect(() => {
    if (!modalAberto) return undefined;
    const previousActiveElement = document.activeElement;
    const frame = requestAnimationFrame(() => {
      detalhesModalCloseRef.current?.focus();
    });
    return () => {
      cancelAnimationFrame(frame);
      if (previousActiveElement && typeof previousActiveElement.focus === 'function') {
        previousActiveElement.focus();
      }
    };
  }, [modalAberto]);

  // Função para atualizar a conta selecionada quando o modal está aberto
  const atualizarContaSelecionada = useCallback(() => {
    if (!contaSelecionada?.id || !modalAberto) return;
    
    // Buscar a conta atualizada nas listas carregadas
    let contasAtualizadas = contasReceber;
    if (filtroAtivo === 'recebidas') {
      contasAtualizadas = contasRecebidas;
    }
    const contaAtualizada = contasAtualizadas.find(c => c.id === contaSelecionada.id);
    
    if (contaAtualizada) {
      // Comparar se houve mudanças significativas (arquivos, status, valores)
      const arquivosAntigos = JSON.stringify(contaSelecionada.arquivos || []);
      const arquivosNovos = JSON.stringify(contaAtualizada.arquivos || []);
      const arquivosDiferentes = arquivosAntigos !== arquivosNovos;
      const statusDiferente = contaAtualizada.status !== contaSelecionada.status;
      const valorDiferente = parseFloat(contaAtualizada.valor_recebido || 0) !== parseFloat(contaSelecionada.valor_recebido || 0);
      
      if (arquivosDiferentes || statusDiferente || valorDiferente) {
        setContaSelecionada(contaAtualizada);
      }
    }
  }, [contaSelecionada, modalAberto, filtroAtivo, contasRecebidas, contasReceber]);

  // Atualização ao abrir o modal (sem polling automático)
  useEffect(() => {
    if (!modalAberto || !contaSelecionada?.id) return;

    let isMounted = true;
    const timeoutId = setTimeout(() => {
      if (isMounted && modalAberto && contaSelecionada?.id) {
        atualizarContaSelecionada();
      }
    }, 300);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [modalAberto, contaSelecionada?.id, atualizarContaSelecionada]);

  const getSelectedCompanyName = () => {
    try {
      const ctx = localStorage.getItem(COMPANY_STORAGE_KEY);
      if (ctx) {
        const parsed = JSON.parse(ctx);
        if (parsed?.companies?.length > 0) {
          return parsed.companies[0].name;
        }
      }
    } catch {}
    return 'MOV Fibra';
  };

  // Função para comprimir imagens
  const compressImage = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          const maxWidth = 1920;
          const maxHeight = 1920;
          const quality = 0.8;

          if (width > maxWidth || height > maxHeight) {
            if (width > height) {
              if (width > maxWidth) {
                height = (height * maxWidth) / width;
                width = maxWidth;
              }
            } else {
              if (height > maxHeight) {
                width = (width * maxHeight) / height;
                height = maxHeight;
              }
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          canvas.toBlob(
            (blob) => resolve(blob || file),
            file.type,
            quality
          );
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  // Função para processar arquivos selecionados
  const handleAdicionarArquivos = async (e) => {
    const files = Array.from(e.target.files || []);
    const allowedMime = new Set(['application/pdf','image/png','image/jpeg','image/jpg','image/gif','image/webp']);
    const MAX_FILE_SIZE = 8 * 1024 * 1024; // 8MB
    const novos = [];
    
    for (const f of files) {
      if (!allowedMime.has(f.type)) {
        showNotification(`Formato não suportado: ${f.name}`, 'error');
        continue;
      }
      
      if (f.size > MAX_FILE_SIZE) {
        const tamanhoMB = (f.size / 1024 / 1024).toFixed(2);
        showNotification(`Arquivo ${f.name} muito grande (${tamanhoMB} MB). Máximo: 8 MB`, 'error');
        continue;
      }
      
      try {
        let fileToProcess = f;
        
        if (f.type.startsWith('image/') && f.type !== 'image/gif') {
          fileToProcess = await compressImage(f);
        }
        
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(fileToProcess);
        });
        
        novos.push({ 
          file: f, 
          descricao: f.name, 
          base64, 
          ready: true 
        });
      } catch (error) {
        showNotification(`Erro ao processar ${f.name}`, 'error');
      }
    }
    
    setArquivosNovos(prev => [...prev, ...novos]);
    e.target.value = '';
  };

  const removerArquivoNovo = (idx) => {
    setArquivosNovos(prev => prev.filter((_, i) => i !== idx));
  };

  // Função para enviar arquivos
  const enviarArquivos = async () => {
    if (arquivosNovos.length === 0 || !contaSelecionada?.id) return;
    
    setUploadingArquivos(true);
    const empresa = getSelectedCompanyName();
    let sucesso = 0;
    let erros = [];
    
    for (const arquivo of arquivosNovos) {
      try {
        await api.post('/n8n/administracao/a_receber/novo_arquivo', {
          id_conta: contaSelecionada.id,
          descricao: arquivo.descricao,
          arquivo_base64: arquivo.base64,
          empresa,
        });
        sucesso++;
      } catch (error) {
        erros.push({ 
          nome: arquivo.descricao, 
          erro: error.response?.data?.message || error.message || 'Erro desconhecido' 
        });
      }
    }
    
    setUploadingArquivos(false);
    
    if (erros.length > 0) {
      showNotification(
        `Arquivos enviados: ${sucesso}. Erros: ${erros.length}`,
        'error'
      );
    } else {
      showNotification(`${sucesso} arquivo(s) adicionado(s) com sucesso!`);
      setArquivosNovos([]);
      await new Promise(resolve => setTimeout(resolve, 500));
      
      if (filtroAtivo === 'recebidas') {
        await carregarRecebidas(true);
      } else {
        await carregarEmAberto(true);
      }
      
      setTimeout(() => {
        atualizarContaSelecionada();
      }, 200);
    }
  };

  const getFiltroArquivosAtual = () => {
    switch (filtroAtivo) {
      case 'todas': return filtroArquivosTodas;
      case 'vencidas': return filtroArquivosVencidas;
      case 'hoje': return filtroArquivosHoje;
      case 'recebidas': return filtroArquivosRecebidas;
      default: return 'todas';
    }
  };
  const setFiltroArquivosAtual = (valor) => {
    switch (filtroAtivo) {
      case 'todas': setFiltroArquivosTodas(valor); break;
      case 'vencidas': setFiltroArquivosVencidas(valor); break;
      case 'hoje': setFiltroArquivosHoje(valor); break;
      case 'recebidas': setFiltroArquivosRecebidas(valor); break;
      default: setFiltroArquivosTodas(valor); break;
    }
  };

  const limparTodosFiltros = () => {
    setBusca('');
    setFiltroTipoRecebimento('todos');
    setFiltroArquivosRecebidas('todas');
    setFiltroArquivosTodas('todas');
    setFiltroArquivosVencidas('todas');
    setFiltroArquivosHoje('todas');
    setFiltroAtivo('todas');
    setRange(getMesAtualRange());
    setPage(1);
  };

  // Função auxiliar para verificar se um arquivo é válido (tem pelo menos um campo não-null)
  const arquivoValido = (arq) => {
    if (!arq || typeof arq !== 'object') return false;
    // Verificar se pelo menos um campo importante não é null ou vazio
    const camposImportantes = ['id', 'descricao', 'arquivo', 'data_envio', 'extensao'];
    return camposImportantes.some(campo => {
      const valor = arq[campo];
      return valor !== null && valor !== undefined && valor !== '';
    });
  };

  const temArquivos = (conta) => {
    if (!conta.arquivos) return false;
    if (Array.isArray(conta.arquivos)) {
      return conta.arquivos.some(arquivoValido);
    }
    try {
      const arquivos = JSON.parse(conta.arquivos);
      return Array.isArray(arquivos) && arquivos.some(arquivoValido);
    } catch {
      return false;
    }
  };
  
  const contarArquivos = (conta) => {
    if (!conta.arquivos) return 0;
    if (Array.isArray(conta.arquivos)) {
      return conta.arquivos.filter(arquivoValido).length;
    }
    try {
      const arquivos = JSON.parse(conta.arquivos);
      return Array.isArray(arquivos) ? arquivos.filter(arquivoValido).length : 0;
    } catch {
      return 0;
    }
  };

  const carregarEmAberto = async (forceRefresh = false, pageNum = 1, append = false) => {
    const empresa = getSelectedCompanyName();
    const requestKey = [
      'a_receber',
      'em_aberto',
      empresa,
      range.start,
      range.end,
      pageNum,
      perPage,
      append ? 'append' : 'replace',
      busca || '',
      filtroAtivo,
      filtroTipoRecebimento,
      filtroArquivosRecebidas,
      filtroArquivosTodas,
      filtroArquivosVencidas,
      filtroArquivosHoje,
    ].join('|');

    if (shouldBlockDuplicateRequest(requestKey)) {
      console.debug('Requisição duplicada de contas em aberto ignorada...');
      return;
    }

    if (carregandoEmAberto) {
      console.debug('Requisição de contas em aberto já em andamento, ignorando...');
      return;
    }

    const agora = Date.now();
    if (ultimaRequisicaoEmAberto && (agora - ultimaRequisicaoEmAberto) < 2000 && !forceRefresh) {
      console.debug('Requisição muito frequente, ignorando...');
      return;
    }

    setCarregandoEmAberto(true);
    setLoading(true);
    setUltimaRequisicaoEmAberto(agora);

    try {
      // Obter filtro de arquivos atual baseado no filtro ativo
      const getFiltroArquivosAtual = () => {
        if (filtroAtivo === 'recebidas') return filtroArquivosRecebidas;
        if (filtroAtivo === 'todas') return filtroArquivosTodas;
        if (filtroAtivo === 'vencidas') return filtroArquivosVencidas;
        if (filtroAtivo === 'hoje') return filtroArquivosHoje;
        return 'todas';
      };

      const params = {
        action: 'em_aberto',
        data_inicio: range.start,
        data_fim: range.end,
        page: pageNum,
        per_page: perPage,
        // Filtros
        busca: busca || undefined,
        filtro_ativo: filtroAtivo !== 'todas' && filtroAtivo !== 'recebidas' ? filtroAtivo : undefined,
        filtro_arquivos: getFiltroArquivosAtual() !== 'todas' ? getFiltroArquivosAtual() : undefined,
        filtro_tipo_recebimento: filtroTipoRecebimento !== 'todos' ? filtroTipoRecebimento : undefined,
        // Ordenação
        sort_by: 'data_vencimento',
        sort_order: 'asc'
      };
      
      // Remover parâmetros undefined
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);
      
      if (forceRefresh) {
        params._t = Date.now();
      }
      
      const response = await api.post('/n8n/administracao/a_receber', params);
      
      if (response.data && response.data.success !== false) {
        const dadosBrutos = response.data.data || [];
        let contas = [];
        dadosBrutos.forEach(item => {
          if (item['contas a receber'] && Array.isArray(item['contas a receber'])) {
            contas = contas.concat(item['contas a receber']);
          }
        });
        const processadas = processarContas(contas);
        const compat = processadas.map(c => ({ ...c, data_vencimento_convertida: c.dataVencimento || c.data_vencimento_convertida }));
        setContasReceber((prev) => (append ? mergeById(prev, compat) : compat));
        setPagination(response.data.pagination || null);
        
        // Atualizar stats do backend (se disponíveis)
        if (response.data.stats) {
          setStats(response.data.stats);
        }
        
        // Atualizar dados do gráfico por dias (se disponíveis)
        if (response.data.grafico_dias) {
          setGraficoDias(response.data.grafico_dias);
        }
        
        if (!append) {
          showNotification(`${compat.length} contas carregadas (página ${pageNum} de ${response.data.pagination?.total_pages || 1})`);
        }
      } else {
        console.warn('API retornou success: false para contas em aberto');
        showNotification('Erro ao carregar contas a receber', 'error');
      }
    } catch (e) {
      console.error('Erro ao carregar contas a receber:', e);
      showNotification('Erro ao carregar contas a receber', 'error');
    } finally {
      setCarregandoEmAberto(false);
      setLoading(false);
    }
  };

  const carregarRecebidas = async (forceRefresh = false, pageNum = 1, append = false) => {
    const empresa = getSelectedCompanyName();
    const requestKey = [
      'a_receber',
      'recebidas',
      empresa,
      range.start,
      range.end,
      pageNum,
      perPage,
      append ? 'append' : 'replace',
      busca || '',
      filtroTipoRecebimento,
      filtroArquivosRecebidas,
    ].join('|');

    if (shouldBlockDuplicateRequest(requestKey)) {
      console.debug('Requisição duplicada de contas recebidas ignorada...');
      return;
    }

    if (carregandoRecebidas) {
      console.debug('Requisição de contas recebidas já em andamento, ignorando...');
      return;
    }

    const agora = Date.now();
    if (ultimaRequisicaoRecebidas && (agora - ultimaRequisicaoRecebidas) < 2000 && !forceRefresh) {
      console.debug('Requisição muito frequente, ignorando...');
      return;
    }

    setCarregandoRecebidas(true);
    setLoading(true);
    setUltimaRequisicaoRecebidas(agora);

    try {
      const params = {
        action: 'recebidas',
        data_inicio: range.start,
        data_fim: range.end,
        page: pageNum,
        per_page: perPage,
        // Filtros
        busca: busca || undefined,
        filtro_arquivos: filtroArquivosRecebidas !== 'todas' ? filtroArquivosRecebidas : undefined,
        filtro_tipo_recebimento: filtroTipoRecebimento !== 'todos' ? filtroTipoRecebimento : undefined,
        // Ordenação por data de recebimento (padrão para recebidas)
        sort_by: 'data_recebimento',
        sort_order: 'asc'
      };
      
      // Remover parâmetros undefined
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);
      
      if (forceRefresh) {
        params._t = Date.now();
      }
      
      const response = await api.post('/n8n/administracao/a_receber', params);
      
      if (response.data && response.data.success !== false) {
        const dadosBrutos = response.data.data || [];
        let contas = [];
        dadosBrutos.forEach(item => {
          if (item['contas a receber'] && Array.isArray(item['contas a receber'])) {
            contas = contas.concat(item['contas a receber']);
          }
        });
        const processadas = processarContas(contas);
        const compat = processadas.map(c => ({ ...c, data_vencimento_convertida: c.dataVencimento || c.data_vencimento_convertida }));
        setContasRecebidas((prev) => (append ? mergeById(prev, compat) : compat));
        setPagination(response.data.pagination || null);
      } else {
        console.warn('API retornou success: false para contas recebidas');
      }
    } catch (e) {
      console.error('Erro ao carregar contas recebidas:', e);
    } finally {
      setCarregandoRecebidas(false);
      setLoading(false);
    }
  };

  // Carregar totais agregados dos próximos 3 meses
  const carregarProximos3Meses = async () => {
    const empresa = getSelectedCompanyName();
    const requestKey = ['a_receber', 'proximos_3_meses', empresa].join('|');

    if (shouldBlockDuplicateRequest(requestKey)) {
      console.debug('Requisição duplicada de próximos 3 meses ignorada...');
      return;
    }

    try {
      const response = await api.post('/n8n/administracao/a_receber', {
        action: 'proximos_3_meses'
      });
      if (response.data?.success && response.data.meses) {
        // Armazenar dados agregados por mês
        setMesesProximos3(response.data.meses);
      } else {
        setMesesProximos3([]);
      }
    } catch (e) {
      console.error('Erro ao carregar próximos 3 meses:', e);
      setMesesProximos3([]);
    }
  };

  // Hook customizado de debounce para evitar múltiplas requisições
  const useDebounce = (callback, delay, deps) => {
    const timeoutRef = useRef(null);
    const callbackRef = useRef(callback);
    const depsKey = deps.join('|');

    useEffect(() => {
      callbackRef.current = callback;
    }, [callback]);
    
    useEffect(() => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      timeoutRef.current = setTimeout(() => {
        callbackRef.current();
      }, delay);
      
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, [delay, depsKey]);
  };

  carregarEmAbertoRef.current = carregarEmAberto;
  carregarRecebidasRef.current = carregarRecebidas;
  carregarProximos3MesesRef.current = carregarProximos3Meses;

  useEffect(() => {
    if (preloadInicialExecutadoRef.current) return;
    preloadInicialExecutadoRef.current = true;

    carregarEmAbertoRef.current();
    carregarRecebidasRef.current();
    carregarProximos3MesesRef.current();
    // Mantém as abas hidratadas logo no primeiro carregamento.
  }, []);

  // Carregar todos os datasets quando o período mudar, com debounce
  useDebounce(() => {
    if (!debounceRangeInicializadoRef.current) {
      debounceRangeInicializadoRef.current = true;
      return;
    }

    if (!carregandoEmAberto && !carregandoRecebidas) {
      carregarEmAbertoRef.current();
      carregarRecebidasRef.current();
      carregarProximos3MesesRef.current();
    }
  }, 500, [range.start, range.end]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT;
      setIsMobile(mobile);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Recarregar dados quando a empresa mudar
  useEffect(() => {
    const handleCompanyChange = () => {
      carregarEmAbertoRef.current(true);
      carregarRecebidasRef.current(true);
      carregarProximos3MesesRef.current();
    };

    window.addEventListener('company-context-changed', handleCompanyChange);
    window.addEventListener('company-changed', handleCompanyChange);

    return () => {
      window.removeEventListener('company-context-changed', handleCompanyChange);
      window.removeEventListener('company-changed', handleCompanyChange);
    };
  }, []);

  // Recarregar quando filtro ativo mudar
  useEffect(() => {
    if (!filtroInicializadoRef.current) {
      filtroInicializadoRef.current = true;
      return;
    }

    setPage(1);
    const filtrosComDados = ['recebidas', 'todas', 'vencidas', 'hoje', 'proxima_vencimento'];
    
    if (filtrosComDados.includes(filtroAtivo)) {
      if (filtroAtivo === 'recebidas') {
        carregarRecebidasRef.current(false, 1);
      } else {
        carregarEmAbertoRef.current(false, 1);
      }
    }
  }, [filtroAtivo]);

  // Recarregar quando perPage mudar
  useEffect(() => {
    // Só recarregar se já tiver dados carregados (evitar recarregar no mount inicial)
    if (!perPageInicializadoRef.current) {
      perPageInicializadoRef.current = true;
      return;
    }

    if (pagination) {
      setPage(1);
      if (filtroAtivo === 'recebidas') {
        carregarRecebidasRef.current(false, 1);
      } else {
        carregarEmAbertoRef.current(false, 1);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perPage]);

  useEffect(() => {
    if (!mobileInicializadoRef.current) {
      mobileInicializadoRef.current = true;
      return;
    }

    setPage(1);
    setPerPage(isMobile ? MOBILE_PER_PAGE : DESKTOP_PER_PAGE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  // Recarregar quando outros filtros mudarem (com debounce para busca)
  useDebounce(() => {
    if (!filtrosDebounceInicializadoRef.current) {
      filtrosDebounceInicializadoRef.current = true;
      return;
    }

    setPage(1);
    const filtrosComDados = ['recebidas', 'todas', 'vencidas', 'hoje', 'proxima_vencimento'];
    
    if (filtrosComDados.includes(filtroAtivo)) {
      if (filtroAtivo === 'recebidas') {
        carregarRecebidasRef.current(false, 1);
      } else {
        carregarEmAbertoRef.current(false, 1);
      }
    }
  }, 500, [busca, filtroTipoRecebimento, filtroArquivosRecebidas, filtroArquivosTodas, filtroArquivosVencidas, filtroArquivosHoje]);

  const getVencimento = (conta) => {
    const base = conta.data_vencimento_convertida || conta.dataVencimento;
    return base ? new Date(base) : null;
  };

  // Função para processar data de recebimento
  const getDataRecebimento = (conta) => {
    if (!conta.data_recebimento) return null;
    
    try {
      const dataStr = String(conta.data_recebimento).trim();
      const matchBrasileiro = dataStr.match(/^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}):(\d{2}))?/);
      if (matchBrasileiro) {
        const [, dia, mes, ano, hora = '00', minuto = '00', segundo = '00'] = matchBrasileiro;
        const dateObj = new Date(`${ano}-${mes}-${dia}T${hora}:${minuto}:${segundo}`);
        if (!isNaN(dateObj.getTime())) {
          return dateObj;
        }
      }
      
      const dateISO = new Date(dataStr);
      if (!isNaN(dateISO.getTime())) {
        return dateISO;
      }
      
      return null;
    } catch {
      return null;
    }
  };

  // As contas já vêm filtradas e paginadas do backend
  // Apenas selecionar a fonte baseada no filtro ativo
  let contasFiltradas = filtroAtivo === 'recebidas' ? contasRecebidas : contasReceber;

  // Usar stats do backend (calculados com todos os dados, não apenas a página atual)
  // Para próximos 3 meses, usar dados agregados do backend
  const totalVencidas = stats.total_vencidas || 0;
  const totalProximas = stats.total_proximas || 0;
  const totalProximosMeses = mesesProximos3.reduce((t, m) => t + (parseFloat(m.valor_total) || 0), 0);

  // Calcular valor do card "Próximas a Vencer" baseado no filtro ativo
  // Se o filtro for específico (hoje, recebidas), usar o total da página filtrada
  // Caso contrário, usar o total_proximas do backend
  const calcularValorCardProximas = () => {
    switch (filtroAtivo) {
      case 'hoje':
      case 'recebidas':
        // Para filtros específicos, calcular com base nas contas filtradas da página atual
        // (Nota: idealmente isso também deveria vir do backend, mas por enquanto mantemos assim)
        return contasFiltradas.reduce((t, c) => t + (parseFloat(c.valor) || 0), 0);
      default:
        // Para o padrão, usar o total_proximas calculado no backend
        return totalProximas;
    }
  };

  const valorCardProximas = calcularValorCardProximas();
  const filtroLabels = {
    todas: 'Todas',
    vencidas: 'Vencidas',
    hoje: 'Vence Hoje',
    proxima_vencimento: 'Próximas 7 dias',
    recebidas: 'Recebidas'
  };
  const filtroAtivoLabel = filtroLabels[filtroAtivo] || 'Todas';

  return (
    <div className="space-y-4 relative min-h-[400px] w-full max-w-full overflow-x-hidden">
      {notification && (
        <NotificationToast type={notification.type} message={notification.message} onClose={() => setNotification(null)} />
      )}

      {/* Loading Overlay - padronizado */}
      {loading && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-[color:var(--bg)]/65 backdrop-blur-[1px]">
          <LoadingSpinner
            fullScreen={false}
            className="min-h-[160px] w-full max-w-md"
            cardClassName="w-full"
            label="Carregando contas a receber"
            subtitle="Aguarde enquanto atualizamos os dados."
          />
        </div>
      )}

      <section
        className="overflow-visible rounded-[var(--cp-radius-card)] border border-[color:var(--cp-border,var(--border))] bg-[color:var(--cp-surface,var(--surface))] [box-shadow:var(--cp-shadow-soft,0_8px_20px_color-mix(in_srgb,var(--text-primary)_12%,transparent))]"
      >
        <FinanceSectionHeader
          sectionLabel="Administrativo"
          title="Contas a Receber"
          badgeLabel={`Visão: ${filtroAtivoLabel}`}
          showCtaDesktop={false}
        />

        <div className="border-b border-[color:var(--cp-border,var(--border))] px-4 py-4 sm:px-5">
          <HeaderStats 
            totalVencidas={totalVencidas} 
            totalProximas={valorCardProximas} 
            totalProximosMeses={totalProximosMeses}
            filtroAtivo={filtroAtivo}
            loading={loading}
          />
        </div>

        <div className="px-4 py-4 sm:px-5">
          <FiltrosOtimizados
            range={range}
            setRange={setRange}
            loading={loading}
            busca={busca}
            setBusca={setBusca}
            filtroTipoRecebimento={filtroTipoRecebimento}
            setFiltroTipoRecebimento={setFiltroTipoRecebimento}
            filtroArquivosAtual={getFiltroArquivosAtual()}
            setFiltroArquivosAtual={setFiltroArquivosAtual}
            onRefresh={() => {
              if (filtroAtivo === 'recebidas') {
                carregarRecebidas(true, page);
              } else {
                carregarEmAberto(true, page);
              }
            }}
            onClearAll={() => {
              limparTodosFiltros();
            }}
            contasFiltradas={contasFiltradas}
            totalContas={pagination?.total || contasReceber.length}
            filtroAtivo={filtroAtivo}
            setFiltroAtivo={setFiltroAtivo}
            contasReceber={contasReceber}
            contasRecebidas={contasRecebidas}
          />
        </div>
      </section>

      <TabelaContasReceber
        contasFiltradas={contasFiltradas}
        abrirModal={abrirModal}
        contarArquivos={contarArquivos}
        filtroAtivo={filtroAtivo}
        onClearFilters={limparTodosFiltros}
      />

      {pagination && pagination.total_pages > 1 && (
        <div className="mt-6">
          <Pagination
            currentPage={page}
            totalPages={pagination.total_pages}
            totalItems={pagination.total}
            itemsPerPage={perPage}
            onPageChange={(newPage) => {
              setPage(newPage);
              if (filtroAtivo === 'recebidas') {
                carregarRecebidas(false, newPage);
              } else {
                carregarEmAberto(false, newPage);
              }
            }}
            onItemsPerPageChange={(newPerPage) => {
              setPerPage(newPerPage);
              setPage(1); // Resetar para primeira página ao mudar itens por página
            }}
            showItemsPerPage={!isMobile}
            itemLabel={pagination.total === 1 ? 'conta' : 'contas'}
          />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <GraficoContasPorDia dados={graficoDias.length > 0 ? graficoDias : processarDadosGraficoDia(contasReceber)} />
        <GraficoContasPorMes dados={mesesProximos3.map(m => ({
          mes: m.mes,
          valor: m.valor_total,
          contas: m.quantidade_contas || 0
        }))} />
      </div>


      {/* Modal de Detalhes */}
      {modalAberto && contaSelecionada && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-2 sm:items-center sm:p-4 overflow-hidden" onKeyDown={handleDetalhesModalKeyDown}>
          <div
            ref={detalhesModalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="detalhes-conta-receber-titulo"
            className="my-0 max-h-[calc(100dvh-1rem)] w-full max-w-4xl overflow-y-auto overscroll-contain rounded-xl bg-[color:var(--surface)] shadow-xl sm:my-auto sm:max-h-[95vh] sm:rounded-lg"
          >
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-[color:var(--border)] sticky top-0 bg-[color:var(--surface)] z-10">
              <h3 id="detalhes-conta-receber-titulo" className="text-base sm:text-lg font-semibold text-[color:var(--text-primary)] pr-2">Detalhes da Conta #{contaSelecionada.id}</h3>
              <button
                ref={detalhesModalCloseRef}
                onClick={fecharModal}
                className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded text-[color:var(--text-muted)] transition-colors hover:text-[color:var(--text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--surface)]"
                aria-label="Fechar detalhes da conta"
              >
                <X className="h-5 w-5 sm:h-6 sm:w-6" />
              </button>
            </div>
            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="rounded-lg border border-[color:var(--border)] bg-[color:var(--surface-muted)] p-3">
                  <p className="text-xs text-[color:var(--text-muted)]">Cliente</p>
                  <p className="text-sm font-semibold text-[color:var(--text-primary)] mt-1 truncate" title={contaSelecionada.cliente || contaSelecionada.fornecedor}>
                    {contaSelecionada.cliente || contaSelecionada.fornecedor}
                  </p>
                </div>
                <div className="rounded-lg border border-[color:var(--border)] bg-[color:var(--surface-muted)] p-3">
                  <p className="text-xs text-[color:var(--text-muted)]">Valor Total</p>
                  <p className="text-sm font-semibold text-[color:var(--text-primary)] mt-1">
                    {formatarMoeda(parseFloat(contaSelecionada.valor) || 0)}
                  </p>
                </div>
                <div className="rounded-lg border border-[color:var(--border)] bg-[color:var(--surface-muted)] p-3">
                  <p className="text-xs text-[color:var(--text-muted)]">Vencimento</p>
                  <p className="text-sm font-semibold text-[color:var(--text-primary)] mt-1">
                    {(() => { const v = getVencimento(contaSelecionada); return v ? v.toLocaleDateString('pt-BR') : 'Data inválida'; })()}
                  </p>
                </div>
                <div className="rounded-lg border border-[color:var(--border)] bg-[color:var(--surface-muted)] p-3">
                  <p className="text-xs text-[color:var(--text-muted)]">Status</p>
                  <p className="text-sm font-semibold text-[color:var(--text-primary)] mt-1">
                    {contaSelecionada.status || 'Não informado'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                {/* Informações Básicas */}
                <div className="space-y-4">
                  <h4 className="font-medium text-[color:var(--text-primary)] border-b pb-2">Informações Básicas</h4>
                  
                  <div>
                    <p className="text-sm font-medium text-[color:var(--text-muted)]">Cliente</p>
                    <p className="text-sm text-[color:var(--text-primary)] mt-1">{contaSelecionada.cliente || contaSelecionada.fornecedor}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm font-medium text-[color:var(--text-muted)]">Descrição</p>
                    <p className="text-sm text-[color:var(--text-primary)] mt-1">{contaSelecionada.observacao || contaSelecionada.descricao}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm font-medium text-[color:var(--text-muted)]">Categoria</p>
                    <p className="text-sm text-[color:var(--text-primary)] mt-1">{contaSelecionada.conta_analitica || 'Não informado'}</p>
                    {contaSelecionada.id_conta_analitica && (
                      <p className="text-xs text-[color:var(--text-muted)] mt-1">ID: {contaSelecionada.id_conta_analitica}</p>
                    )}
                  </div>
                  
                  <div>
                    <p className="text-sm font-medium text-[color:var(--text-muted)]">Planejamento</p>
                    <p className="text-sm text-[color:var(--text-primary)] mt-1">{contaSelecionada.planejamento || 'Não informado'}</p>
                    {contaSelecionada.id_planejamento && (
                      <p className="text-xs text-[color:var(--text-muted)] mt-1">ID: {contaSelecionada.id_planejamento}</p>
                    )}
                  </div>
                  
                  <div>
                    <p className="text-sm font-medium text-[color:var(--text-muted)]">Documento</p>
                    <p className="text-sm text-[color:var(--text-primary)] mt-1">{contaSelecionada.documento || 'Não informado'}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm font-medium text-[color:var(--text-muted)]">Tipo de Recebimento</p>
                    <p className="text-sm text-[color:var(--text-primary)] mt-1">{contaSelecionada.tipo_recebimento || 'Não informado'}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm font-medium text-[color:var(--text-muted)]">Conta Bancária</p>
                    <p className="text-sm text-[color:var(--text-primary)] mt-1">{contaSelecionada.conta || 'Não informado'}</p>
                  </div>
                </div>

                {/* Informações Financeiras */}
                <div className="space-y-4">
                  <h4 className="font-medium text-[color:var(--text-primary)] border-b pb-2">Informações Financeiras</h4>
                  
                  <div>
                    <p className="text-sm font-medium text-[color:var(--text-muted)]">Valor em Aberto</p>
                    <p className="text-lg font-semibold text-red-600 mt-1">
                      {formatarMoeda(parseFloat(contaSelecionada.valor_aberto) || 0)}
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-sm font-medium text-[color:var(--text-muted)]">Valor Recebido</p>
                    <p className="text-lg font-semibold text-green-600 mt-1">
                      {formatarMoeda(parseFloat(contaSelecionada.valor_recebido) || 0)}
                    </p>
                  </div>
                  
                  {(() => {
                    const dataRecebimentoStr = contaSelecionada.data_recebimento || contaSelecionada.dataRecebimento;
                    if (!dataRecebimentoStr) return null;
                    
                    return (
                      <div>
                        <p className="text-sm font-medium text-[color:var(--text-muted)]">Data de Recebimento</p>
                        <p className="text-sm text-[color:var(--text-primary)] mt-1">
                          {(() => {
                            const dataRecebimento = getDataRecebimento({ data_recebimento: dataRecebimentoStr });
                            if (dataRecebimento) {
                              const dataFormatada = dataRecebimento.toLocaleDateString('pt-BR');
                              const hora = dataRecebimento.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
                              return hora !== '00:00' ? `${dataFormatada} ${hora}` : dataFormatada;
                            }
                            return dataRecebimentoStr;
                          })()}
                        </p>
                      </div>
                    );
                  })()}
                  
                  <div>
                    <p className="text-sm font-medium text-[color:var(--text-muted)]">Dias para Vencimento</p>
                    <p className={`text-sm font-medium mt-1 ${
                      contaSelecionada.diasVencimento < 0 ? 'text-red-600' : 
                      contaSelecionada.diasVencimento <= 7 ? 'text-orange-600' : 'text-green-600'
                    }`}>
                      {contaSelecionada.status === 'recebida' || contaSelecionada.status === 'cancelada' || contaSelecionada.status === 'recebida_parcialmente' ? 
                        'Conta processada' :
                        contaSelecionada.diasVencimento < 0 ? 
                        `Vencida há ${Math.abs(contaSelecionada.diasVencimento)} dias` :
                        contaSelecionada.diasVencimento === 0 ? 'Vence hoje' :
                        `Vence em ${contaSelecionada.diasVencimento} dias`
                      }
                    </p>
                  </div>
                </div>
              </div>

              {/* Arquivos */}
              <div className="mt-6 w-full max-w-full overflow-x-hidden">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 mb-4">
                  <h4 className="font-medium text-[color:var(--text-primary)] border-b pb-2 flex-1">Arquivos Anexados</h4>
                  {arquivosNovos.length > 0 && (
                    <button
                      onClick={enviarArquivos}
                      disabled={uploadingArquivos}
                      className="w-full sm:w-auto min-h-[44px] px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/35"
                    >
                      {uploadingArquivos ? 'Enviando...' : `Enviar ${arquivosNovos.length} arquivo(s)`}
                    </button>
                  )}
                </div>
                
                {temArquivos(contaSelecionada) && (
                  <div className="space-y-3 mb-4 w-full max-w-full overflow-x-hidden">
                    {Array.isArray(contaSelecionada.arquivos) && contaSelecionada.arquivos.map((arquivo, index) => (
                      <ArquivoItem key={arquivo.id || index} arquivo={arquivo} contaId={contaSelecionada.id} />
                    ))}
                  </div>
                )}
                
                <div className="border-t pt-4">
                  <label className="block text-sm font-medium text-[color:var(--text-primary)] mb-2">Adicionar Novo Arquivo</label>
                  <div className="relative">
                    <div className="border-2 border-dashed border-[color:var(--border)] rounded-lg p-4 text-center hover:border-[color:var(--accent)] hover:bg-blue-50/30 transition-all duration-200">
                      <input 
                        type="file" 
                        accept="application/pdf,image/*" 
                        multiple 
                        onChange={handleAdicionarArquivos}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                        disabled={uploadingArquivos}
                      />
                      <div className="pointer-events-none">
                        <Upload className="h-5 w-5 mx-auto text-[color:var(--text-muted)] mb-2" />
                        <p className="text-sm text-[color:var(--text-muted)] mb-1">
                          Clique para selecionar arquivos
                        </p>
                        <p className="text-xs text-[color:var(--text-muted)]">
                          PDF, PNG, JPG, JPEG, GIF, WEBP | Máx: 8 MB
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {arquivosNovos.length > 0 && (
                    <div className="mt-3 space-y-3">
                      {arquivosNovos.map((arquivo, idx) => {
                        const getFileIcon = () => {
                          const fileName = arquivo.file.name.toLowerCase();
                          if (fileName.endsWith('.pdf')) return <FileText className="h-4 w-4 text-red-500" />;
                          if (['.png', '.jpg', '.jpeg', '.gif', '.webp'].some(ext => fileName.endsWith(ext))) {
                            return <ImageIcon className="h-4 w-4 text-blue-500" />;
                          }
                          return <File className="h-4 w-4 text-[color:var(--text-muted)]" />;
                        };
                        
                        return (
                          <div key={idx} className="border rounded-lg p-3 bg-[color:var(--surface-muted)]">
                            <div className="flex items-start gap-3 mb-2">
                              <div className="flex-shrink-0 mt-0.5">
                                {getFileIcon()}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-[color:var(--text-primary)] truncate" title={arquivo.file.name}>
                                  {arquivo.file.name}
                                </p>
                                <p className="text-xs text-[color:var(--text-muted)] mt-0.5">
                                  {((arquivo.file.size || 0) / 1024 / 1024).toFixed(2)} MB
                                </p>
                              </div>
                              <button 
                                onClick={() => removerArquivoNovo(idx)}
                                className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center flex-shrink-0 rounded-lg text-red-600 hover:text-red-800 hover:bg-[color:var(--surface)] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/35"
                                disabled={uploadingArquivos}
                                title="Remover arquivo"
                                aria-label={`Remover arquivo ${arquivo.file.name}`}
                              >
                                <XCircle className="h-4 w-4" />
                              </button>
                            </div>
                            <div className="mt-2">
                              <label className="block text-xs font-medium text-[color:var(--text-primary)] mb-1">
                                Descrição
                              </label>
                              <input
                                type="text"
                                value={arquivo.descricao || ''}
                                onChange={(e) => setArquivosNovos(prev => 
                                  prev.map((x, i) => i === idx ? { ...x, descricao: e.target.value } : x)
                                )}
                                placeholder="Adicione uma descrição para o arquivo"
                                disabled={uploadingArquivos}
                                className="w-full min-h-[44px] px-3 py-2 text-sm border border-[color:var(--border)] rounded-lg focus:ring-2 focus:ring-[color:var(--accent)] focus:border-transparent disabled:bg-[color:var(--surface-muted)] disabled:cursor-not-allowed"
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Ações */}
              <div className="mt-6 flex flex-col sm:flex-row justify-end gap-3">
                <button
                  onClick={fecharModal}
                  className="w-full sm:w-auto min-h-[44px] px-4 py-2 text-sm font-medium text-[color:var(--text-primary)] bg-[color:var(--surface-muted)] border border-[color:var(--border)] rounded-md hover:bg-[color:var(--surface)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)]"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContasReceber;
